"use client";

import Image from "next/image";
import { useStoreDetail } from "@/hooks/use-stores";
import type { ApiCartItem, AddToCartPayload, PromoCodeListItem } from "@/types";
import { formatAmount } from "@/lib/money";

/**
 * Stable signature of an add-on selection. Two cart lines with the same product
 * AND the same signature are the same configuration, so their quantities should
 * merge into one line instead of stacking up as duplicates.
 */
function addonSignature(
  options: { addon_option_ulid: string; quantity?: number }[]
): string {
  return options
    .map((o) => `${o.addon_option_ulid}:${o.quantity ?? 1}`)
    .sort()
    .join("|");
}

/** Signature of a cart line already in the cart. */
export function cartItemAddonSignature(item: ApiCartItem): string {
  return addonSignature((item.addons ?? []).flatMap((a) => a.options ?? []));
}

/** Signature of an add-to-cart payload, comparable to `cartItemAddonSignature`. */
export function payloadAddonSignature(addons: AddToCartPayload["addons"]): string {
  return addonSignature((addons ?? []).flatMap((a) => a.options ?? []));
}

/** Find an existing cart line with the identical product + add-on configuration. */
export function findMatchingCartItem(
  items: ApiCartItem[] | undefined,
  productUlid: string,
  addons: AddToCartPayload["addons"]
): ApiCartItem | undefined {
  const target = payloadAddonSignature(addons);
  return items?.find(
    (i) => i.product_ulid === productUlid && cartItemAddonSignature(i) === target
  );
}

/** Resolve item price from real API fields */
export function itemPrice(item: ApiCartItem): number {
  // item.subtotal = quantity * discounted_price (per-item total)
  if (item.subtotal > 0) return item.subtotal;
  if (item.discounted_price > 0) return item.discounted_price * item.quantity;
  if (item.product_price > 0) return item.product_price * item.quantity;
  return 0;
}

/** Per-line original (pre-discount) total, for strikethrough display. */
export function itemOriginalPrice(item: ApiCartItem): number {
  return (item.product_price > 0 ? item.product_price : item.discounted_price) * item.quantity;
}

/** True when the product is discounted vs its base price. */
export function itemHasDiscount(item: ApiCartItem): boolean {
  return item.product_price > 0 && item.discounted_price > 0 && item.discounted_price < item.product_price;
}

/** Readable summary of the selected add-on options, e.g. "Coca Cola, 2× Frites". */
export function itemAddonsSummary(item: ApiCartItem): string {
  if (!item.addons?.length) return "";
  return item.addons
    .flatMap((addon) => addon.options ?? [])
    .map((opt) => (opt.quantity > 1 ? `${opt.quantity}× ${opt.name}` : opt.name))
    .filter(Boolean)
    .join(", ");
}

/** Resolve item image from real API fields */
export function itemImage(item: ApiCartItem): string | null {
  return item.product_image ?? null;
}

export function ItemThumbnail({ item }: { item: ApiCartItem }) {
  const img = itemImage(item);
  return (
    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-gray-100 dark:bg-[#313131]">
      <Image
        src={img ?? "/fast-logo.svg"}
        alt={item.product_name}
        fill
        className={img ? "object-cover" : "object-contain p-2.5 opacity-20 dark:opacity-10"}
        sizes="56px"
      />
    </div>
  );
}

export function promoCodeLabel(promo: PromoCodeListItem): string {
  return promo.promo_code ?? promo.code ?? promo.name ?? promo.title ?? "CODE";
}

export function promoDescription(promo: PromoCodeListItem): string {
  if (promo.description) return promo.description;

  const minOrder = promo.min_order_cart ?? promo.minimum_order;
  if (promo.discount_type === "percentage" && promo.value != null) {
    return minOrder ? `${promo.value}% off for orders over ${formatAmount(minOrder)} DA.` : `${promo.value}% off.`;
  }
  if (promo.discount_amount != null) {
    return minOrder ? `${formatAmount(promo.discount_amount)} DA off for orders over ${formatAmount(minOrder)} DA.` : `${formatAmount(promo.discount_amount)} DA off.`;
  }

  return "Offer available on eligible orders.";
}

export function CartAvgTime({ storeUlid }: { storeUlid: string }) {
  const { data: store } = useStoreDetail(storeUlid);
  if (!store?.avg_time) return null;
  return <> · {store.avg_time} min</>;
}
